import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { pusherConfig, isRealtimeRouteEnabled } from '../config/pusher';
import { StatusSession } from '../constants';
import type { PanelStore } from '../store/interfaces';
import { clearChannelRegistry } from './channel-registry';
import {
  bindPusherConnectionState,
  disconnectPusherClient,
  getInitialPusherConnectionState,
  getPusherClient,
} from './pusher-client';
import type { RealtimeConnectionState, RealtimeContextValue } from './types';

interface RealtimeProviderProps {
  children: ReactNode;
}

export const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export const RealtimeProvider = ({ children }: RealtimeProviderProps) => {
  const { pathname } = useLocation();
  const sessionStatus = useSelector((state: PanelStore.RootState) => state.auth.sessionStatus);
  const token = useSelector((state: PanelStore.RootState) => state.auth.token);
  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>('idle');
  const merchantMe = useSelector((state: PanelStore.RootState) => state.dashboard.merchantMe);
  const isKYC2Approved = useSelector(
    (state: PanelStore.RootState) => state.dashboard.merchantMe?.kycReviewStatus === 'APPROVED'
  );

  const isEnabledOnRoute = isRealtimeRouteEnabled(pathname);
  const isValidSession = sessionStatus === StatusSession.LOGGED_IN && Boolean(token);

  /* If the KYC2 is approved, the user will not connect to Pusher */
  const shouldConnect = Boolean(pusherConfig.enabled && isEnabledOnRoute && isValidSession && !isKYC2Approved && merchantMe);
  const [client, setClient] = useState(() => getPusherClient(shouldConnect));
  console.log({ shouldConnect, isKYC2Approved, merchantMe });

  const disconnect = useCallback(() => {
    const existingClient = getPusherClient(shouldConnect);
    if (existingClient) {
      clearChannelRegistry(existingClient);
    }

    disconnectPusherClient();
    setClient(null);
    setConnectionState('disconnected');
  }, []);

  const connect = useCallback(() => {
    if (!shouldConnect) {
      disconnect();
      return;
    }

    const nextClient = getPusherClient(shouldConnect);
    setClient(nextClient);

    if (!nextClient) {
      setConnectionState('idle');
      return;
    }

    setConnectionState(getInitialPusherConnectionState());
  }, [disconnect, shouldConnect]);

  useEffect(() => {
    if (!shouldConnect) {
      return;
    }
    connect();
  }, [connect]);

  useEffect(() => {
    if (!client) {
      return undefined;
    }

    const unbindStateChange = bindPusherConnectionState(setConnectionState);
    return () => {
      if (unbindStateChange) {
        unbindStateChange();
      }
    };
  }, [client]);

  useEffect(
    () => () => {
      disconnect();
    },
    [disconnect]
  );

  const contextValue = useMemo<RealtimeContextValue>(
    () => ({
      client,
      isConfigured: pusherConfig.enabled,
      isEnabledOnRoute,
      isConnected: connectionState === 'connected',
      connectionState,
      connect,
      disconnect,
    }),
    [client, isEnabledOnRoute, connectionState, connect, disconnect]
  );

  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
};
